<script setup lang="ts">
import { HelpTextTooltip, TextField } from '@haskoning/vue-component-library'
import { useCalculationFormComposable } from '@/components/calculation-form/composable/useCalculationFormComposable'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { computed, defineProps } from 'vue'
import type { Choice } from '@/components/form/type/type'
import type { ShipType } from '@/stores/type/ship.type'

const shipStore = useShipStore()
const { items: shipItems } = storeToRefs(shipStore)

const props = defineProps<CalculationFormMixinProps>()
const emit = defineEmits(['updateTabCompletion'])
const { calculationStore, currentItem, isValid, formRef, disabled, isReadOnly, hasHelpText } =
  useCalculationFormComposable(props, emit)
defineExpose({ formRef })

const projectInformationFields = ['name', 'still_water_level']

const shipField = computed(() => {
  return calculationStore.fields['vessel']
})

const label = computed<string>(() => {
  return shipField.value?.label || ''
})

const shipChoices = computed<Choice[]>(() => {
  return shipItems.value.map((item: ShipType) => {
    return {
      value: item.id,
      display_name: item.name
    }
  })
})

const shipValue = computed<number | null>({
  get() {
    if (!currentItem.value['vessel'].length) return null
    return currentItem.value['vessel'][0]
  },
  set: async (value) => {
    if (value) {
      currentItem.value['vessel'] = [value]
      await calculationStore.update(currentItem.value)
    }
  }
})
</script>

<template>
  <v-form ref="formRef" v-model="isValid" :disabled="disabled" :readonly="isReadOnly">
    <v-card elevation="0" flat>
      <v-card-title class="header-font d-flex justify-space-between align-center">
        General
        <div class="d-flex justify-end">
          <slot name="resetInputButton" />
        </div>
      </v-card-title>
      <v-card-subtitle class="description-font">
        Parameters specific to the project
      </v-card-subtitle>

      <v-card-text>
        <span class="sub-header-font"> Project information </span>
        <v-row align="center" no-gutters v-for="field in projectInformationFields" :key="field">
          <v-col cols="12" md="8" lg="4" class="d-flex align-center">
            <HelpTextTooltip
              v-if="hasHelpText(field)"
              :text="calculationStore.fields[field].help_text || ''"
            />
            <span :class="hasHelpText(field) ? 'px-2' : 'px-0'">
              {{ calculationStore.fields[field].label }}
            </span>
          </v-col>

          <v-col cols="12" md="8" lg="4" class="pt-0">
            <TextField
              :field="calculationStore.fields[field]"
              :model-value="currentItem"
              :no-help-text="true"
              :readonly="isReadOnly"
              @update="calculationStore.update(currentItem)"
            />
          </v-col>
        </v-row>

        <span class="sub-header-font"> Vessel selection </span>

        <v-row class="pt-4" no-gutters>
          <v-col cols="12" md="8" lg="4" class="d-flex align-center">
            <HelpTextTooltip
              v-if="hasHelpText('vessel')"
              :text="calculationStore.fields['vessel'].help_text || ''"
            />
            <span :class="hasHelpText('vessel') ? 'px-2' : 'px-0'">
              {{ calculationStore.fields['vessel'].label }}
            </span>
          </v-col>
          <v-col cols="12" md="8" lg="4" class="pt-0">
            <v-select
              :rules="[(value: number) => (value !== null && value !== undefined) || 'Required.']"
              :label="label"
              color="primary"
              :items="shipChoices"
              :readonly="isReadOnly"
              item-value="value"
              item-title="display_name"
              v-model="shipValue"
              @click.stop
            >
            </v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<style scoped></style>
